import { useMemo, useState } from 'react';
import { Globe2, MapPin, X } from 'lucide-react';

import type { AdminStore } from '@/client/types/stores';
import { useStoreDeliveryCoverageQuery } from '@/client/api/stores/parity.query';
import { useUpdateStoreDelivery } from '@/client/api/stores/parity.mutation';
import { PERMISSIONS } from '@/client/config/permissions';
import { useGate } from '@/client/hooks/useGate';

import { SectionCard, CardSkeleton, ListSkeleton } from '../shared';
import { IdLookup } from '../../IdLookup';
import { ReasonDialog } from '../ReasonDialog';
import { AttributesSection } from './AttributesSection';
import { SaveRow } from './controls';

/**
 * Where the store delivers (`GET/PUT /<id>/delivery-locations`). Zones are
 * location ids; ff-api applies the seller's granularity rules (no zone finer
 * than a locality, a cap on count). No zones = delivers everywhere.
 */
const CoverageCard: React.FC<{ store: AdminStore }> = ({ store }) => {
  const { data, isLoading } = useStoreDeliveryCoverageQuery(store.storeid);
  const initial = useMemo(() => data?.lids ?? [], [data]);
  const [lids, setLids] = useState<number[] | null>(null);
  const [draft, setDraft] = useState('');
  // Names of zones added this session; saved zones get theirs from the API.
  const [picked, setPicked] = useState<Map<number, string>>(new Map());
  const [confirm, setConfirm] = useState(false);
  const save = useUpdateStoreDelivery();
  const canEdit = useGate(PERMISSIONS.store.settings).allowed;

  if (isLoading || !data) return <CardSkeleton description><ListSkeleton rows={3} trailing /></CardSkeleton>;

  const current = lids ?? initial;
  const names = new Map(data.zones.map((z) => [z.lid, z.name ?? `Location #${z.lid}`]));
  const dirty = JSON.stringify([...current].sort()) !== JSON.stringify([...initial].sort());
  const add = (n: number) => {
    if (Number.isInteger(n) && n > 0 && !current.includes(n)) setLids([...current, n]);
    setDraft('');
  };

  return (
    <SectionCard title="Delivery coverage" description="Regions or towns this store delivers to.">
      {current.length === 0 ? (
        <p className="mb-3 flex items-center gap-2 text-sm text-gray-600"><Globe2 className="h-4 w-4 text-teal-600" /> Delivers everywhere (no zones set).</p>
      ) : (
        <div className="mb-3 flex flex-wrap gap-2">
          {current.map((lid) => (
            <span key={lid} className="inline-flex items-center gap-1 rounded-full bg-teal-50 px-2.5 py-1 text-xs font-medium text-teal-800">
              <MapPin className="h-3 w-3" /> {names.get(lid) ?? picked.get(lid) ?? `Location #${lid}`}
              {canEdit && (
                <button type="button" onClick={() => setLids(current.filter((x) => x !== lid))} aria-label="Remove zone" className="text-teal-600 hover:text-teal-900">
                  <X className="h-3 w-3" />
                </button>
              )}
            </span>
          ))}
        </div>
      )}
      {canEdit && (
        <div className="flex items-start gap-2">
          {/* Picking a result adds it straight away; a typed id needs the button. */}
          <IdLookup
            key={current.length}
            kind="location"
            value={draft}
            onChange={setDraft}
            onPick={(hit) => { setPicked((m) => new Map(m).set(hit.id, hit.label)); add(hit.id); }}
            placeholder="Search town or region, or type a lid"
            className="w-72"
          />
          <button type="button" onClick={() => add(Number(draft))} disabled={!draft} className="h-10 rounded-lg border border-gray-200 px-3 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-40">Add zone</button>
        </div>
      )}
      <SaveRow dirty={dirty} saving={save.isPending} onSave={() => setConfirm(true)} onReset={() => setLids(null)} />
      {confirm && (
        <ReasonDialog
          open
          title="Change delivery coverage"
          description={current.length ? `${current.length} zone(s).` : 'Clears all zones — the store will deliver everywhere.'}
          confirmLabel="Save"
          reasonRequired
          loading={save.isPending}
          onConfirm={(reason) => save.mutate({ storeid: store.storeid, lids: current, reason }, { onSuccess: () => { setConfirm(false); setLids(null); } })}
          onClose={() => setConfirm(false)}
        />
      )}
    </SectionCard>
  );
};

export const DeliverySection: React.FC<{ store: AdminStore }> = ({ store }) => (
  <div className="space-y-4">
    <CoverageCard store={store} />
    <AttributesSection store={store} groups={['delivery']} title="Delivery options" description="Delivery mode and details shown to buyers." />
  </div>
);
